import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'
import { useAuth } from '../contexts/AuthContext'

export default function Agenda() {
    const { user, profile } = useAuth()
    const isMaster = profile?.user_role === 'master'
    const navigate = useNavigate()

    const [deals, setDeals] = useState([])
    const [empresas, setEmpresas] = useState([])
    const [empresaSelecionada, setEmpresaSelecionada] = useState(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        if (!user) return
        const params = {}
        if (isMaster) {
            params.is_master = true
            if (empresaSelecionada) params.company_id = empresaSelecionada
        } else {
            params.is_master = false
            params.company_id = profile?.company_id
        }
        setLoading(true)
        api.get('/deals', { params }).then(r => {
            setDeals(r.data)
            setLoading(false)
        })
    }, [user, isMaster, empresaSelecionada, profile]) // eslint-disable-line

    useEffect(() => {
        if (isMaster) api.get('/companies').then(r => setEmpresas(r.data))
    }, [isMaster])

    const handleResolvido = async (deal) => {
        await api.put(`/deals/${deal.id}`, {
            data_agendamento: null,
            user_id: user.id,
            user_name: profile?.name
        })
        await api.post('/deal-activities', {
            deal_id: deal.id,
            user_id: user.id,
            user_name: profile?.name,
            tipo: 'resolvido',
            descricao: `✅ Compromisso marcado como resolvido`
        })
        setDeals(prev => prev.map(d => d.id === deal.id ? { ...d, data_agendamento: null } : d))
    }

    const hoje = new Date()
    hoje.setHours(0, 0, 0, 0)
    const amanha = new Date(hoje)
    amanha.setDate(amanha.getDate() + 1)

    const agendados = deals
        .filter(d => d.data_agendamento && d.status === 'open')
        .map(d => ({ ...d, dataObj: new Date(d.data_agendamento + 'T12:00:00') }))
        .sort((a, b) => a.dataObj - b.dataObj)

    const passados = agendados.filter(d => d.dataObj < hoje)
    const deHoje = agendados.filter(d => d.dataObj >= hoje && d.dataObj < amanha)
    const deAmanha = agendados.filter(d => d.dataObj >= amanha && d.dataObj < new Date(amanha.getTime() + 86400000))
    const proximos = agendados.filter(d => d.dataObj >= new Date(amanha.getTime() + 86400000))

    const empresaAtual = empresas.find(e => e.id === empresaSelecionada)

    const CardAgenda = ({ deal, destaque }) => (
        <div className={`bg-gray-900 rounded-xl p-4 border transition ${destaque === 'hoje' ? 'border-cyan-600 bg-cyan-900/10' : destaque === 'atrasado' ? 'border-red-700 bg-red-900/10' : 'border-gray-800 hover:border-gray-600'}`}>
            <div className="flex justify-between items-start gap-3">
                <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                        {destaque === 'hoje' && <span className="text-xs font-bold text-cyan-400 bg-cyan-900/40 px-2 py-0.5 rounded-full">🔔 HOJE</span>}
                        {destaque === 'atrasado' && <span className="text-xs font-bold text-red-400 bg-red-900/40 px-2 py-0.5 rounded-full">⚠️ ATRASADO</span>}
                        {deal.produto && <span className="text-xs text-indigo-300 bg-indigo-900/40 px-2 py-0.5 rounded-full">{deal.produto}</span>}
                        {deal.origem && <span className="text-xs text-yellow-300 bg-yellow-900/40 px-2 py-0.5 rounded-full">{deal.origem}</span>}
                    </div>
                    <p className="font-semibold text-white">{deal.title}</p>
                    <div className="flex flex-wrap gap-3 mt-2 text-xs text-gray-400">
                        {deal.contato && <span>👤 {deal.contato}</span>}
                        {deal.segmento && <span>🏷️ {deal.segmento}</span>}
                        {deal.localizacao && <span className="text-orange-300">📍 {deal.localizacao}</span>}
                    </div>
                    {deal.observacoes && <p className="text-xs text-gray-500 mt-2 bg-gray-800 rounded-lg px-3 py-2">📝 {deal.observacoes}</p>}
                </div>
                <div className="text-right shrink-0">
                    <p className="text-cyan-400 font-bold text-sm">🗓️ {deal.dataObj.toLocaleDateString('pt-BR')}</p>
                    {deal.value && <p className="text-emerald-400 text-xs mt-1">R$ {Number(deal.value).toLocaleString('pt-BR')}</p>}
                    <button
                        onClick={() => navigate('/', { state: { openDealId: deal.id } })}
                        className="mt-2 text-xs text-indigo-400 hover:text-indigo-300 transition block">
                        Ver no Pipeline →
                    </button>
                </div>
            </div>
            {(destaque === 'atrasado' || destaque === 'hoje') && (
                <div className="mt-3 pt-3 border-t border-gray-800">
                    <button
                        onClick={() => handleResolvido(deal)}
                        className="w-full bg-emerald-800/40 hover:bg-emerald-700 text-emerald-300 hover:text-white text-xs py-1.5 rounded-lg transition font-semibold">
                        ✅ Marcar como Resolvido
                    </button>
                </div>
            )}
        </div>
    )

    const Secao = ({ titulo, icon, deals, destaque }) => {
        if (deals.length === 0) return null
        return (
            <div className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                    <span className="text-lg">{icon}</span>
                    <h2 className="font-bold text-base">{titulo}</h2>
                    <span className="bg-gray-800 text-gray-400 text-xs px-2 py-0.5 rounded-full">{deals.length}</span>
                </div>
                <div className="space-y-3">
                    {deals.map(deal => <CardAgenda key={deal.id} deal={deal} destaque={destaque} />)}
                </div>
            </div>
        )
    }

    return (
        <div>
            <div className="flex justify-between items-start mb-6">
                <div>
                    <h1 className="text-xl md:text-2xl font-bold">📅 Agenda</h1>
                    <p className="text-gray-400 text-sm mt-1">Compromissos e visitas agendadas</p>
                </div>
            </div>

            {isMaster && (
                <div className="flex flex-wrap items-center gap-3 mb-6 bg-gray-900 rounded-xl p-3 border border-gray-800">
                    <span className="text-sm text-gray-400">🏢 Empresa:</span>
                    <select className="bg-gray-800 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 ring-indigo-500 flex-1 min-w-0"
                        value={empresaSelecionada || ''} onChange={e => setEmpresaSelecionada(e.target.value || null)}>
                        <option value="">Todas as empresas</option>
                        {empresas.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                    </select>
                    {empresaSelecionada && <button onClick={() => setEmpresaSelecionada(null)} className="text-xs text-gray-500 hover:text-white transition">✕</button>}
                    {empresaAtual && <span className="text-xs text-indigo-400 bg-indigo-900/30 px-3 py-1 rounded-full">🏢 {empresaAtual.name}</span>}
                </div>
            )}

            {loading ? (
                <div className="text-center py-16 text-gray-500">
                    <p className="text-2xl animate-pulse">📅</p>
                    <p className="mt-2 text-sm">Carregando agenda...</p>
                </div>
            ) : agendados.length === 0 ? (
                <div className="text-center py-16 text-gray-500">
                    <p className="text-4xl mb-3">📅</p>
                    <p>Nenhum compromisso agendado.</p>
                    <p className="text-sm mt-1">Adicione uma data de agendamento nos cards do Pipeline!</p>
                </div>
            ) : (
                <div>
                    <Secao titulo="Atrasados" icon="⚠️" deals={passados} destaque="atrasado" />
                    <Secao titulo="Hoje" icon="🔔" deals={deHoje} destaque="hoje" />
                    <Secao titulo="Amanhã" icon="📆" deals={deAmanha} destaque="amanha" />
                    <Secao titulo="Próximos compromissos" icon="🗓️" deals={proximos} destaque="futuro" />
                </div>
            )}
        </div>
    )
}